import { useActivities } from "@/hooks/useActivities";
import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import Pagination from "@/components/common/Pagination";
import { istDateOf, fmtTimeIST, fmtDayLabel } from "@/lib/dateRange";
import { Phone, MessageCircle, MapPin, Presentation, StickyNote, FileText, Activity as ActIcon } from "lucide-react";

const ICONS = { "Phone Call": Phone, WhatsApp: MessageCircle, "Field Visit": MapPin, Demo: Presentation, Note: StickyNote, "Proposal Sent": FileText };

function groupByDay(items) {
  const groups = [];
  items.forEach((a) => {
    const day = istDateOf(a.when || a.created_at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(a);
    else groups.push({ day, items: [a] });
  });
  return groups;
}

export default function ActivityTimeline({ apiParams, type, refreshKey }) {
  const { items, total, page, setPage, loading, error, reload, pageSize } = useActivities(apiParams, type, 30, refreshKey);
  if (loading) return <Loading label="Loading timeline…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!items.length) return <EmptyState title="No activities" description="Logged calls, WhatsApp, visits and notes appear here." icon={ActIcon} />;

  return (
    <div className="space-y-6" data-testid="activity-timeline-view">
      {groupByDay(items).map((g) => (
        <section key={g.day} data-testid={`timeline-day-${g.day}`}>
          <h3 className="sticky top-0 z-10 bg-[#F8FAFC]/90 backdrop-blur py-1 text-xs font-bold uppercase tracking-wider text-slate-500">{fmtDayLabel(g.day)} · {g.items.length}</h3>
          <ol className="mt-2 relative border-l-2 border-slate-200 ml-3 space-y-4">
            {g.items.map((a) => {
              const Icon = ICONS[a.type] || ActIcon;
              return (
                <li key={a.id} className="relative ml-6" data-testid={`timeline-${a.id}`}>
                  <span className="absolute -left-[38px] top-3 grid place-items-center h-6 w-6 rounded-full bg-white border-2 border-teal text-teal"><Icon className="h-3 w-3" /></span>
                  <div className="bg-white border border-slate-200/80 rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-slate-800">{a.type}{a.lead_name ? <span className="font-normal text-slate-500"> · {a.lead_name}</span> : null}</span>
                      <span className="text-xs text-slate-400 shrink-0">{fmtTimeIST(a.when || a.created_at)}</span>
                    </div>
                    <div className="text-xs text-slate-400">{a.user_name}{a.outcome ? ` · ${a.outcome}` : ""}</div>
                    {a.notes && <div className="text-sm text-slate-600 mt-1">{a.notes}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      <div className="text-sm text-slate-500">{total} total</div>
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </div>
  );
}
